import { CSSResult, html, TemplateResult, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { formatDate, formatDateToISO } from '~/modules/utils/date-formater';
import { formatRichText } from '~/modules/utils/rich-text.helper';

import DERModuleElement from '~/modules/utils/der-module-element';

import { SvgUtility } from '~/ui/utils';

import { styles as globalStyles } from '~/ui/scss/main.styles';
import { styles } from './der-tailormade-appointment-booking.styles';

import { ProgressBarButtonConfig } from '~/ui/components/progress-bar/progress-bar-item/types';
import {
    AppointmentBookingData,
    AppointmentBookingFormData,
    AppointmentBookingTranslations,
    Environment,
    LanguageMap,
    OpeningHour,
    OpeningHours,
    SalesSiteResponse,
    SpecialOpeningHour,
    Variant,
} from './appointment-booking.interface';

import { DateUtility } from '~/ui/components/calendar/date-utility';

import '~/ui/components/module-title';
import '~/ui/components/progress-bar';
import './date-picker';
import './request-form';
import './sales-sites';
import './selected-data';
import './time-picker';

import ProgressBar from '~/ui/components/progress-bar';
import DatePicker from './date-picker';
import RequestForm from './request-form';
import TimePicker from './time-picker';
import { DEFAULT_OPENING_HOURS } from './opening-hours.defaults';
import { withOpportunities } from '~/modules/utils/opportunity-mixin';

export class AppointmentBooking extends withOpportunities(DERModuleElement) {
    static override styles: CSSResult[] = [unsafeCSS(globalStyles), unsafeCSS(styles)];

    override opportunitySource: string = '948150003';
    private _translationsLoading?: Promise<void>;

    @property({ type: Number, attribute: 'booking-day-offset' })
    bookingDayOffset: number = 3;

    @property({ type: String, attribute: 'contact-language' })
    contactLanguage: string = '';

    @property({ type: String, attribute: 'form-name' })
    formName: string = 'appointmentBookingForm';

    @property({ type: Boolean, attribute: 'hide-module-title' })
    hideModuleTitle: boolean = false;

    override opportunityOwningTeam: string = '';

    override opportunitySourceUrl: string = '';

    override opportunityType: string = '';

    override opportunityOriginatingObjectTag: string = 'der-tailormade-appointment-booking';

    @property({ type: String, attribute: 'variant' })
    variant: Variant = 'default';

    /**
     * Environment control (Production/Test). For internal development purposes only.
     * @default 'production'
     * @private
     */
    @property({ type: String, attribute: 'x-environment' })
    xEnvironment: Environment = 'production';

    @property({ type: String, attribute: 'sales-site-url' })
    salesSiteUrl: string = 'https://dtch-tailormade.azurewebsites.net/api/branch-get-salessites';

    @property({ type: String, attribute: 'submit-url' })
    submitUrl: string = 'https://dtch-tailormade.azurewebsites.net/api/appointment-booking-request-send-booking-data';

    @state()
    openingHours: OpeningHours = DEFAULT_OPENING_HOURS;

    @state()
    openingWeekdays: number[] = [];

    @state()
    specialSelectableDates: string[] = [];

    @state()
    salesSite: SalesSiteResponse = {} as SalesSiteResponse;

    @state()
    showConfirmation: boolean = false;

    @state()
    selectionData: AppointmentBookingFormData = {} as AppointmentBookingFormData;

    @state()
    salesSiteIdentifier: string | undefined = undefined;

    @state()
    private isSubmitting = false;

    @state()
    translations: AppointmentBookingTranslations = {
        businessComponent: {
            supTitle: '',
            title: '',
            intro: '',
            headlines: { timepicker: '', form: '' },
        },
        confirmation: { title: '', subtitle: '', text: '', text2: '', closing: '' },
        datepicker: {
            title: '',
            saleSite: { placeholder: '' },
            headlines: { consultationChannel: '', datepicker: '' },
            tabList: { typeOffice: '', typePhone: '', typeTeams: '' },
        },
        timepicker: { title: '' },
        form: {
            title: '',
            salutation: '',
            methodOfContactLabel: '',
            genders: { female: '', male: '', other: '' },
            fields: { firstName: '', lastName: '', phone: '', email: '', description: '' },
            methodOfContact: { email: '', phone: '' },
            opportunityCostConfirmation: '',
            validation: { errorSpaces: '', errorPhone: '' },
            submitButton: '',
            requestDataProtectionLink: '',
        },
    };

    @state()
    private _submitButton: ProgressBarButtonConfig = {
        type: 'button',
        config: { label: 'Submit' },
        clickFn: () => this._handleSubmitClick(),
    };

    @query('der-ui-progress-bar') progressBarComponent!: ProgressBar;
    @query('#date-form') dateForm!: HTMLFormElement;
    @query('der-tailormade-appointment-booking-date-picker') datePickerComponent!: DatePicker;
    @query('der-tailormade-appointment-booking-time-picker') timePickerComponent!: TimePicker;
    @query('der-tailormade-appointment-booking-request-form') requestComponent!: RequestForm;
    @query('.m-appointment-booking__confirmation') private _confirmationElement?: HTMLElement;

    /**
     * Initializes component data and loads required translations.
     * Called by Lit-Element lifecycle.
     */
    override connectedCallback(): void {
        super.connectedCallback();
        this._prepareTranslations();
    }

    /**
     * Updates component when properties or states change.
     * Automatically loads corresponding data when properties like
     * 'opportunityOwningTeam' or 'lang' change.
     *
     * @param changedProperties Map of changed properties
     */
    override async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        if (changedProperties.has('opportunityOwningTeam') && this.opportunityOwningTeam) {
            this.salesSiteIdentifier = this.opportunityOwningTeam;
            this.selectionData.OpportunityOwningTeam = this.opportunityOwningTeam;
        }
        if (this.salesSiteIdentifier) {
            if (changedProperties.has('salesSiteIdentifier') || changedProperties.has('salesSiteUrl')) {
                await this._getSalesSiteById();
                this._setOpeningWeekdays();
                this._setOpeningHours();
                this._setSpecialSelectableDates();
            }
        }
        if (changedProperties.has('lang')) {
            this._prepareTranslations(true);
        }
    }

    private _assignValue(obj: Partial<AppointmentBookingFormData>): void {
        Object.assign(this.selectionData, obj);
        this.requestUpdate();
    }

    /**
     * Dispatches a custom analytics event to track appointment booking completion.
     *
     * Sends a composed and bubbling CustomEvent containing detailed booking information
     * to enable analytics tracking and measurement of booking completion. This event
     * is dispatched after successful form submission to capture user interaction data.
     *
     * @private
     * @emits der-analytics CustomEvent with detailed booking analytics data
     *
     */
    private _dispatchAnalyticsEvent(data: AppointmentBookingData): void {
        const reducedAnalyticsData = {
            selectedDate: formatDateToISO(data.OpportunityConsultationDate),
            selectedTime: data.OpportunityConsultationTime,
            email: data.ContactEmail,
        };
        /**
         *
         */
        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: 'form-submitted',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    action: 'confirmation view after form submit',
                    category: '',
                    data: reducedAnalyticsData,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Collects and combines all appointment booking data into a single object.
     *
     * Gathers both user input data (from forms and pickers) and internal configuration
     * data to create a complete appointment booking dataset. This data will be used for
     * submission to the server and analytics events.
     *
     * @private
     * @returns {AppointmentBookingData} Complete appointment booking data object with user inputs and metadata
     *
     * @throws {Error} If selection data or additional data cannot be retrieved
     */
    private _getAppointmentBookingData(): AppointmentBookingData {
        this._setOpportunitySourceUrl();
        this._setContactLanguage();

        const additionalData: Omit<AppointmentBookingData, keyof AppointmentBookingFormData> = {
            formName: this.formName,
            ContactLanguage: this.contactLanguage,
            OpportunitySource: this.opportunitySource,
            OpportunitySourceURL: this.opportunitySourceUrl,
            OpportunityType: this.opportunityType,
            OpportunityOriginatingObjectTag: this.opportunityOriginatingObjectTag,
            variant: this.variant,
        };

        return {
            ...this.selectionData,
            ...additionalData,
        };
    }

    /**
     * Sets available opening days based on current
     * sales branch opening hours.
     *
     * @private
     */
    private _setOpeningWeekdays(): void {
        if (!this.salesSite.openingHours) {
            this.openingWeekdays = [1, 2, 3, 4, 5, 6];
            // Clear owning team if branch has no opening hours
            this._assignValue({ OpportunityOwningTeam: '' });
            return;
        }
        this.openingWeekdays = this.salesSite.openingHours.defaultOpeningHours.map(
            (entry: OpeningHour): number => entry.weekday
        );
    }

    private _setOpeningHours(): void {
        if (!this.salesSite.openingHours) {
            return;
        }

        this.openingHours = this.salesSite.openingHours;
    }

    /**
     * Sets special selectable dates from special opening hours.
     * Only includes dates where isOpen is true.
     *
     * @private
     */
    private _setSpecialSelectableDates(): void {
        if (!this.salesSite?.openingHours?.specialOpeningHours) {
            this.specialSelectableDates = [];
            return;
        }

        this.specialSelectableDates = this.salesSite.openingHours.specialOpeningHours
            .filter((special: SpecialOpeningHour) => special.isOpen)
            .map((special: SpecialOpeningHour) => {
                try {
                    const normalizedDate = DateUtility.getDateString(special.date);
                    if (DateUtility.isValidDateString(normalizedDate)) {
                        return normalizedDate;
                    }
                    return null;
                } catch (error) {
                    console.warn('Error parsing special opening hour date:', special.date, error);
                    return null;
                }
            })
            .filter((date: string | null): date is string => date !== null);
    }

    private _setOpportunitySourceUrl(): void {
        this.opportunitySourceUrl = this.opportunitySourceUrl || (top || window)?.location?.href || '';
    }

    /**
     * Fetches sales branch data by team ID parameter.
     * Uses Fetch-API to load data from server.
     * Logs error message in case of failure.
     *
     * @private
     */
    private async _getSalesSiteById(): Promise<void> {
        const url: URL = new URL(this.salesSiteUrl);
        url.searchParams.set('teamId', this.salesSiteIdentifier ?? '');
        url.searchParams.set('lang', this.lang ?? '');

        const response: Response = await fetch(url.toString());
        if (!response.ok) {
            this.error(this.tagName, {
                message: 'Can not load sales-site data',
                teamId: this.salesSiteIdentifier,
            });
        }

        this.salesSite = await response.json();
    }

    private _scrollToConfirmation(): void {
        this.updateComplete.then(() => {
            if (this._confirmationElement) {
                this._confirmationElement.scrollIntoView({ behavior: 'smooth' });
            }
        });
    }

    private _setElementTranslations(): void {
        this._submitButton.config!.label = this.translations.form.submitButton;
    }

    /**
     * Updates the component's translation strings based on current language settings.
     *
     * Replaces the existing translation object with the current language data, ensuring
     * all UI text is displayed correctly in the selected language. This method is
     * automatically called when the component language or translations change.
     *
     * @private
     * @throws {Error} If required translation data is not available
     */
    private _setTranslations(): void {
        this.translations = {
            businessComponent: {
                supTitle: this.trans('bcSupTitle'),
                title: this.trans('bcTitle'),
                intro: this.trans('bcIntro'),
                headlines: {
                    timepicker: this.trans('bcHeadlineTimepicker'),
                    form: this.trans('bcHeadlineRequest'),
                },
            },
            confirmation: {
                title: this.trans('confirmationTitle'),
                subtitle: this.trans('confirmationSubtitle'),
                text: this.trans('confirmationText'),
                text2: this.trans('confirmationText2'),
                closing: this.trans('confirmationClosing'),
            },
            datepicker: {
                title: this.trans('progressBarItemTitleStepOne'),
                saleSite: {
                    placeholder: this.trans('saleSiteSelectPlaceholder'),
                },
                headlines: {
                    consultationChannel: this.trans('bcHeadlineConsultationChannel'),
                    datepicker: this.trans('bcHeadlineCalendar'),
                },
                tabList: {
                    typeOffice: this.trans('opportunityTypeOffice'),
                    typePhone: this.trans('opportunityTypePhone'),
                    typeTeams: this.trans('opportunityTypeTeams'),
                },
            },
            timepicker: {
                title: this.trans('progressBarItemTitleStepTwo'),
            },
            form: {
                title: this.trans('progressBarItemTitleStepThree'),
                salutation: this.trans('requestSalutation'),
                methodOfContactLabel: this.trans('requestMethodOfContactLabel'),
                fields: {
                    email: this.trans('requestEMail'),
                    firstName: this.trans('requestFirstName'),
                    lastName: this.trans('requestLastName'),
                    phone: this.trans('requestPhone'),
                    description: this.trans('requestDescriptionLabel'),
                },
                genders: {
                    female: this.trans('requestGenderFemale'),
                    male: this.trans('requestGenderMale'),
                    other: this.trans('requestGenderOther'),
                },
                methodOfContact: {
                    email: this.trans('requestMethodEmail'),
                    phone: this.trans('requestMethodPhone'),
                },
                opportunityCostConfirmation: this.trans('requestOpportunityCostConfirmation'),
                validation: {
                    errorPhone: this.trans('formValidationErrorPhone'),
                    errorSpaces: this.trans('formValidationErrorSpaces'),
                },
                submitButton: this.trans('submitButton'),
                requestDataProtectionLink: this.trans('requestDataProtectionLink'),
            },
        };

        this._setElementTranslations();
    }

    /**
     * Handles the selection of an appointment date in the DatePicker component.
     * Validates the date and proceeds to next booking step if valid.
     *
     * @param event CustomEvent with selected date and booking data
     */
    private _handleDatePickerSelection(event: CustomEvent): void {
        event.stopPropagation();
        if (event.detail.value && this._isDateFormValid()) {
            // Reset time picker when date changes
            this.timePickerComponent.reset();
            this.progressBarComponent.showNextItem();
        }
    }

    /**
     * Handles the selection of a new sales branch.
     * Resets relevant state values and triggers re-initialization
     * of components.
     *
     * @param event CustomEvent with ID of selected sales branch
     */
    private _handleSalesSiteChange(event: CustomEvent): void {
        event.stopPropagation();
        this.openingWeekdays = [];
        this.datePickerComponent.reset();
        this.timePickerComponent.reset();
        this.salesSiteIdentifier = event.detail.salesSite;
    }

    /**
     * Handles the confirmation of the contact form and initiates
     * the submission process or displays the confirmation view.
     *
     * Validates the form, sends data to server and
     * displays appropriate confirmation or error messages.
     *
     * @returns false if submission was aborted, otherwise boolean
     */
    private _handleSubmitClick(): boolean {
        if (this.showConfirmation || this.isSubmitting || !this.requestComponent.validate()) {
            return false;
        }

        // Mark submission started to prevent double clicks
        this.isSubmitting = true;

        try {
            this._assignValue(this.requestComponent.getFormValue());

            // Direct confirmation path (no network call)
            if (this.selectionData.ContactEmailConfirmation) {
                this.showConfirmation = true;
                this._scrollToConfirmation();
                // Safe to unlock immediately (synchronous path)
                this.isSubmitting = false;
            } else {
                const data: AppointmentBookingData = this._getAppointmentBookingData();
                /**
                 * Custom Event: der-analytics
                 * Purpose: Notify global analytics layer about a validated form submission.
                 * detail structure:
                 *   name            Module identifier ('form-submit')
                 *   element_name    Tag name of the component (lowercase)
                 *   element_id      DOM id if present
                 *   variant         Component variant
                 *   action          Interaction type ('form submit clicked')
                 *   category        Event category ('form')
                 *   data            Payload sent to backend (merged static + user form values)
                 */
                this.dispatchEvent(
                    new CustomEvent('der-analytics', {
                        detail: {
                            name: 'form-submit',
                            element_name: this.tagName.toLowerCase(),
                            element_id: this.id ?? '',
                            variant: this.variant,
                            action: 'form submit clicked',
                            category: 'form',
                            data: data,
                        },
                        bubbles: true,
                        composed: true,
                    })
                );

                fetch(this.submitUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-environment': this.xEnvironment },
                    body: JSON.stringify(data),
                })
                    .then((response: Response): void => {
                        if (response.ok) {
                            this.showConfirmation = true;
                            this._scrollToConfirmation();
                            this._dispatchAnalyticsEvent(data);
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('Error in form submission:', error);
                    })
                    .finally(() => {
                        // Unlock after async completes
                        this.isSubmitting = false;
                    });
            }
        } catch (error) {
            console.error('Error in form submission:', error);
            // In case of synchronous error unlock again
            this.isSubmitting = false;
        }
        return false;
    }

    private _handleTimePickerSelection(event: CustomEvent): void {
        event.stopPropagation();
        if (this._isTimePickerValid()) {
            this.progressBarComponent.showNextItem();
        }
    }

    private _isDateFormValid(): boolean {
        if (!this.datePickerComponent.validate()) {
            return false;
        }

        this._assignValue(this.datePickerComponent.getFormValue());
        return true;
    }

    private _isTimePickerValid(): boolean {
        if (!this.timePickerComponent.validate()) {
            return false;
        }

        this._assignValue({ OpportunityConsultationTime: this.timePickerComponent.selectedTimeSlot });

        return true;
    }

    private _setContactLanguage(): void {
        const languageMap: LanguageMap = {
            'fr-CH': '948150001',
            'de-CH': '948150000',
        };
        this.contactLanguage = this.contactLanguage || languageMap[this.lang] || '';
    }

    /**
     * Loads and prepares translation data for the component.
     *
     * This method ensures that the component has access to the current language translations
     * before rendering. It initiates the translation loading process and updates the
     * component's UI text when translations are ready. The method supports force reloading
     * of translations when needed.
     *
     * @private
     * @param {boolean} [force=false] - Optional flag to force reloading of translations,
     * bypassing any existing loading promise
     */
    private _prepareTranslations(force: boolean = false): void {
        if (this._translationsLoading && !force) return;
        this._translationsLoading = this.loadTranslation(import.meta.url, `i18n/${this.lang}.json`)
            .then(() => {
                this._setTranslations();
                this.requestUpdate();
            })
            .catch((err: Error) => {
                const errorMessage = `Could not load translations for lang '${this.lang}' via ${import.meta.url} and i18n/${this.lang}.json: ${err}`;
                this.warn(this.tagName, { message: 'Translation load failed', errorMessage });
            });
    }

    override render(): TemplateResult | undefined {
        return html`
            <div class="m-appointment-booking">
                <div class="m-appointment-booking__content">
                    ${!this.hideModuleTitle
                        ? html`
                              <div class="m-appointment-booking__information">
                                  <der-ui-module-title
                                      suptitle="${this.translations.businessComponent.supTitle}"
                                      title="${this.translations.businessComponent.title}"
                                      intro="${this.translations.businessComponent.intro}"
                                      ?inverted=${true}
                                  ></der-ui-module-title>
                              </div>
                          `
                        : ''}
                    <div class="m-appointment-booking__progress-bar">
                        ${!this.showConfirmation
                            ? html` <div class="m-appointment-booking__progress-bar-wrapper">
                                  <der-ui-progress-bar lang=${this.lang}>
                                      <der-ui-progress-bar-item
                                          label="${this.translations?.datepicker.title}"
                                          .nextButton=${{
                                              type: 'link',
                                              clickFn: () => this._isDateFormValid(),
                                          }}
                                      >
                                          <der-tailormade-appointment-booking-date-picker
                                              lang="${this.lang}"
                                              booking-day-offset="${this.bookingDayOffset}"
                                              sales-site-url="${this.salesSiteUrl}"
                                              opportunity-owning-team="${ifDefined(this.opportunityOwningTeam)}"
                                              .openingWeekdays="${this.openingWeekdays}"
                                              .specialSelectableDates="${this.specialSelectableDates}"
                                              .translations="${this.translations.datepicker}"
                                              @calendar-date-selected=${this._handleDatePickerSelection}
                                              @sales-site-selected=${this._handleSalesSiteChange}
                                          ></der-tailormade-appointment-booking-date-picker>
                                      </der-ui-progress-bar-item>
                                      <der-ui-progress-bar-item
                                          label="${this.translations?.timepicker.title}"
                                          .nextButton=${{
                                              type: 'link',
                                              clickFn: () => this._isTimePickerValid(),
                                          }}
                                      >
                                          <div class="m-appointment-booking__progress-bar-item-wrapper">
                                              <div class="m-appointment-booking__selection-wrapper">
                                                  ${this.salesSite.name
                                                      ? html`<der-tailormade-appointment-booking-selected-data>
                                                            <div>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 40 40"
                                                                >
                                                                    ${SvgUtility.systemPin}
                                                                </svg>
                                                                <span> ${this.salesSite.name} </span>
                                                            </div>
                                                        </der-tailormade-appointment-booking-selected-data>`
                                                      : ``}
                                                  <der-tailormade-appointment-booking-selected-data>
                                                      <div>
                                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                              ${SvgUtility.systemCalendar()}
                                                          </svg>
                                                          <span
                                                              >${formatDate(
                                                                  this.selectionData.OpportunityConsultationDate,
                                                                  this.lang
                                                              )}</span
                                                          >
                                                      </div>
                                                  </der-tailormade-appointment-booking-selected-data>
                                              </div>
                                              <div class="m-appointment-booking__subitem-wrapper">
                                                  <div class="m-appointment-booking__subheadline">
                                                      ${this.translations?.businessComponent.headlines.timepicker}
                                                  </div>

                                                  <der-tailormade-appointment-booking-time-picker
                                                      selected-date="${ifDefined(
                                                          this.selectionData.OpportunityConsultationDate
                                                      )}"
                                                      .openingHours="${this.openingHours}"
                                                      .translations="${this.translations.datepicker}"
                                                      @der.selected=${this._handleTimePickerSelection}
                                                  ></der-tailormade-appointment-booking-time-picker>
                                              </div>
                                          </div>
                                      </der-ui-progress-bar-item>
                                      <der-ui-progress-bar-item
                                          label="${this.translations?.form.title}"
                                          .nextButton=${{
                                              type: 'button',
                                              config: { label: this.translations.form.submitButton },
                                              clickFn: () => this._handleSubmitClick(),
                                          } as ProgressBarButtonConfig}
                                      >
                                          <div class="m-appointment-booking__progress-bar-item-wrapper">
                                              <div class="m-appointment-booking__selection-wrapper">
                                                  ${this.salesSite.name
                                                      ? html`<der-tailormade-appointment-booking-selected-data>
                                                            <div>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 40 40"
                                                                >
                                                                    ${SvgUtility.systemPin}
                                                                </svg>
                                                                <span> ${this.salesSite.name} </span>
                                                            </div>
                                                        </der-tailormade-appointment-booking-selected-data>`
                                                      : ``}

                                                  <der-tailormade-appointment-booking-selected-data>
                                                      <div>
                                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                              ${SvgUtility.systemCalendar()}
                                                          </svg>
                                                          <span
                                                              >${formatDate(
                                                                  this.selectionData.OpportunityConsultationDate,
                                                                  this.lang
                                                              )}</span
                                                          >
                                                      </div>
                                                      <div>
                                                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                              ${SvgUtility.systemClock}
                                                          </svg>
                                                          <span>${this.selectionData.OpportunityConsultationTime}</span>
                                                      </div>
                                                  </der-tailormade-appointment-booking-selected-data>
                                              </div>
                                              <div class="m-appointment-booking__subitem-wrapper">
                                                  <div class="m-appointment-booking__subheadline">
                                                      ${this.translations?.businessComponent.headlines.form}
                                                  </div>
                                                  <div class="m-appointment-booking__form-wrapper">
                                                      <der-tailormade-appointment-booking-request-form
                                                          lang="${this.lang}"
                                                          variant="${this.variant}"
                                                          @der.submit="${this._handleSubmitClick}"
                                                          .translations="${this.translations.form}"
                                                      ></der-tailormade-appointment-booking-request-form>
                                                  </div>
                                              </div>
                                          </div>
                                      </der-ui-progress-bar-item>
                                  </der-ui-progress-bar>
                              </div>`
                            : html`
                                  <div class="m-appointment-booking__confirmation">
                                      <div class="m-appointment-booking__confirmation-top">
                                          <div class="m-appointment-booking__confirmation-icon">
                                              <i>
                                                  <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="72"
                                                      height="72"
                                                      viewBox="0 0 40 40"
                                                  >
                                                      ${SvgUtility.systemCheckFull}
                                                  </svg>
                                              </i>
                                          </div>
                                          <div class="m-appointment-booking__confirmation-header">
                                              <div class="m-appointment-booking__confirmation-headline">
                                                  ${this.translations?.confirmation.title}
                                              </div>
                                              <div class="m-appointment-booking__confirmation-text">
                                                  ${unsafeHTML(formatRichText(this.translations?.confirmation.text))}
                                              </div>
                                          </div>
                                      </div>

                                      <div class="m-appointment-booking__confirmation-info">
                                          <div class="m-appointment-booking__confirmation-subheadline">
                                              ${this.translations?.confirmation.subtitle}
                                          </div>
                                          ${this.salesSite.name
                                              ? html`<der-tailormade-appointment-booking-selected-data>
                                                    <div>
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                            ${SvgUtility.systemPin}
                                                        </svg>
                                                        <span>
                                                            ${this.salesSite.name}, ${this.salesSite.address},
                                                            ${this.salesSite.zip} ${this.salesSite.city}
                                                        </span>
                                                    </div>
                                                </der-tailormade-appointment-booking-selected-data>`
                                              : ``}
                                          <der-tailormade-appointment-booking-selected-data>
                                              <div>
                                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                      ${SvgUtility.systemCalendar()}
                                                  </svg>
                                                  <span
                                                      >${formatDate(
                                                          this.selectionData.OpportunityConsultationDate,
                                                          this.lang
                                                      )}</span
                                                  >
                                              </div>
                                              <div>
                                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">
                                                      ${SvgUtility.systemClock}
                                                  </svg>
                                                  <span>${this.selectionData.OpportunityConsultationTime}</span>
                                              </div>
                                          </der-tailormade-appointment-booking-selected-data>
                                      </div>

                                      <div class="m-appointment-booking__confirmation-bottom">
                                          <div class="m-appointment-booking__confirmation-text">
                                              ${unsafeHTML(formatRichText(this.translations?.confirmation.text2))}
                                          </div>
                                          <div class="m-appointment-booking__confirmation-closing">
                                              ${this.translations?.confirmation.closing}
                                          </div>
                                      </div>
                                  </div>
                              `}
                    </div>
                </div>
            </div>
        `;
    }
}
